import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useAuth } from '../../hooks/useAuth';
import { Permission } from '../../types/auth';
import Button from '../common/Button';

interface PermissionGateProps {
  permission: Permission | Permission[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoginPrompt?: boolean;
  showUpgradePrompt?: boolean;
}

/**
 * A component that only renders its children if the current user has the required permission(s)
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  children,
  fallback,
  showLoginPrompt = false,
  showUpgradePrompt = false,
}) => {
  const { hasPermission, isAuthenticated, upgradeToPremium } = useAuth();

  // Convert single permission to array
  const permissions = Array.isArray(permission) ? permission : [permission];

  // Check if user has all required permissions
  const hasAllPermissions = permissions.every((p) => hasPermission(p));

  if (hasAllPermissions) {
    return <>{children}</>;
  }

  // If a fallback is provided, render that
  if (fallback) {
    return <>{fallback}</>;
  }

  // If no fallback is provided but showLoginPrompt is true and user is not authenticated
  if (showLoginPrompt && !isAuthenticated) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Login Required</Text>
        <Text style={styles.message}>
          You need to be logged in to access this feature.
        </Text>
        <Button
          title="Login"
          onPress={() => {}} // This should navigate to login screen
          style={styles.button}
        />
      </View>
    );
  }

  // If no fallback is provided but showUpgradePrompt is true and user is authenticated
  if (showUpgradePrompt && isAuthenticated) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Premium Feature</Text>
        <Text style={styles.message}>
          This feature is only available to premium users.
        </Text>
        <Button
          title="Upgrade to Premium"
          onPress={upgradeToPremium}
          style={styles.button}
        />
      </View>
    );
  }

  // Default fallback (empty view)
  return null;
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1F2937',
  },
  message: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 16,
  },
  button: {
    minWidth: 200,
  },
});

export default PermissionGate;
